import { useMemo } from 'react';
import { useEntitySchemaContext } from './EntitySchemaContext';
import {
  type EntitySchemaApiId,
  type EntitySchemaSlug,
  type EntitySchemaV1,
  type EntityType,
} from '@/model/EntitySchema.model';

interface UseEntitySchemaFieldsResponse {
  entityFields: Array<EntitySchemaV1>;
  entityLoading: boolean;
  entityUpdating: boolean;
  hasEntityError: boolean;
}

interface UseEntitySchemaActionsResponse {
  setFieldRequired: (apiId: EntitySchemaApiId, required: boolean) => Promise<void>;
  entityUpdating: boolean;
  hasEntityError: boolean;
}

export type EntityFieldRequiredMap = Record<EntitySchemaSlug, boolean>;

export const useEntitySchemaFields = (): UseEntitySchemaFieldsResponse => {
  const { entityFields, entityLoading, entityUpdating, hasEntityError } = useEntitySchemaContext();
  return {
    entityFields,
    entityLoading,
    entityUpdating,
    hasEntityError,
  };
};

export const useEntitySchemaActions = (): UseEntitySchemaActionsResponse => {
  const { setFieldRequired, entityUpdating, hasEntityError } = useEntitySchemaContext();
  return { setFieldRequired, entityUpdating, hasEntityError };
};

export const useEntitySchemaFieldsByType = (entityType: EntityType): EntityFieldRequiredMap => {
  const { entityFields } = useEntitySchemaContext();

  return useMemo(
    () =>
      entityFields.reduce<EntityFieldRequiredMap>((acc, field) => {
        if (field.entity_type === entityType) {
          acc[field.slug] = field.required;
        }
        return acc;
      }, {} as EntityFieldRequiredMap),
    [entityFields, entityType]
  );
};
